// Works out which forEach loop each workflow step belongs to (its parentStepId).
//
// A loop's matching endLoop is found by walking the graph breadth-first with nesting-depth
// tracking. That walk must follow only the normal flow (next / true / false edges). An error
// edge is a jump to a handler and may leave the loop it starts in: RB3718 has a step inside
// the "For each line to stamp" loop whose error handler is a step AFTER that loop's endLoop.
// Following that edge reached the OUTER loop's endLoop first, so the inner loop claimed the
// outer loop's endLoop and everything between, and the outer loop was left without one
// ("Missing EndLoop for RS104").
//
// Handlers that only error edges reach (so no normal path leads to them) still belong to the
// loop of the step that routes to them, so they are claimed in a second pass.

const isErrorEdge = (edge) => edge.sourceHandle === 'error';

function indexEdges(edges) {
  const flow = new Map();
  const all = new Map();
  edges.forEach((edge) => {
    if (!all.has(edge.source)) all.set(edge.source, []);
    all.get(edge.source).push(edge);
    if (!isErrorEdge(edge)) {
      if (!flow.has(edge.source)) flow.set(edge.source, []);
      flow.get(edge.source).push(edge);
    }
  });
  return { flow, all };
}

// Outermost loops first, so an inner loop's children overwrite its outer loop's.
function sortLoopsByDepth(forEachIds, nodeById, allEdges) {
  const depths = new Map();
  forEachIds.forEach((forEachId) => {
    const visited = new Set();
    const queue = [{ nodeId: 'start', depth: 0 }];
    while (queue.length > 0) {
      const { nodeId, depth } = queue.shift();
      if (visited.has(nodeId)) continue;
      visited.add(nodeId);
      if (nodeId === forEachId) {
        depths.set(forEachId, depth);
        break;
      }
      const newDepth = nodeById.get(nodeId)?.data.stepType === 'forEach' ? depth + 1 : depth;
      (allEdges.get(nodeId) || []).forEach((edge) => {
        if (!visited.has(edge.target)) queue.push({ nodeId: edge.target, depth: newDepth });
      });
    }
  });
  return [...forEachIds].sort((a, b) => (depths.get(a) || 0) - (depths.get(b) || 0));
}

function nextDepth(stepType, depth, isLoopStart) {
  if (stepType === 'forEach' && !isLoopStart) return depth + 1;
  if (stepType === 'endLoop') return depth - 1;
  return depth;
}

function findMatchingEndLoop(forEachId, nodeById, flowEdges) {
  const visited = new Set();
  const queue = [{ nodeId: forEachId, depth: 0 }];
  while (queue.length > 0) {
    const { nodeId, depth } = queue.shift();
    if (visited.has(nodeId)) continue;
    visited.add(nodeId);
    const stepType = nodeById.get(nodeId)?.data.stepType;
    if (stepType === 'endLoop' && depth === 0) return nodeId;
    const newDepth = nextDepth(stepType, depth, nodeId === forEachId);
    (flowEdges.get(nodeId) || []).forEach((edge) => {
      if (!visited.has(edge.target)) queue.push({ nodeId: edge.target, depth: newDepth });
    });
  }
  return null;
}

// Every step between the loop and its matching endLoop (the endLoop itself included),
// following normal flow only.
function collectLoopMembers(forEachId, nodeById, flowEdges) {
  const members = [];
  const visited = new Set();
  const queue = [{ nodeId: forEachId, depth: 0 }];
  while (queue.length > 0) {
    const { nodeId, depth } = queue.shift();
    if (visited.has(nodeId)) continue;
    visited.add(nodeId);
    if (nodeId !== forEachId && nodeId !== 'start') members.push(nodeId);
    const stepType = nodeById.get(nodeId)?.data.stepType;
    if (stepType === 'endLoop' && depth === 0) continue; // the matching endLoop: do not go past it
    const newDepth = nextDepth(stepType, depth, nodeId === forEachId);
    (flowEdges.get(nodeId) || []).forEach((edge) => queue.push({ nodeId: edge.target, depth: newDepth }));
  }
  return members;
}

// Handlers reached only through error edges (no normal path leads to them) belong to the
// loop of the step that routes to them. A handler that is already part of some loop's normal
// flow, such as one that jumps out to a step after an inner loop, is left to that loop.
function collectOrphanHandlers(members, claimed, allEdges) {
  const orphans = [];
  const seen = new Set();
  const queue = [];
  members.forEach((id) => {
    (allEdges.get(id) || []).filter(isErrorEdge).forEach((edge) => queue.push(edge.target));
  });
  while (queue.length > 0) {
    const nodeId = queue.shift();
    if (seen.has(nodeId) || claimed.has(nodeId)) continue;
    seen.add(nodeId);
    orphans.push(nodeId);
    (allEdges.get(nodeId) || []).forEach((edge) => queue.push(edge.target));
  }
  return orphans;
}

export function computeParentStepMap(nodes, edges) {
  const nodeById = new Map(nodes.map((node) => [node.id, node]));
  const forEachIds = nodes.filter((node) => node.data.stepType === 'forEach').map((node) => node.id);
  const { flow, all } = indexEdges(edges);

  const loopMembers = new Map();
  const claimed = new Set();
  forEachIds.forEach((forEachId) => {
    if (!findMatchingEndLoop(forEachId, nodeById, flow)) return;
    const members = collectLoopMembers(forEachId, nodeById, flow);
    loopMembers.set(forEachId, members);
    members.forEach((id) => claimed.add(id));
  });

  const parentStepMap = new Map();
  sortLoopsByDepth(forEachIds, nodeById, all).forEach((forEachId) => {
    const members = loopMembers.get(forEachId);
    if (!members) return;
    const orphans = collectOrphanHandlers(members, claimed, all);
    [...members, ...orphans].forEach((id) => parentStepMap.set(id, forEachId));
  });
  return parentStepMap;
}
